import React, { useRef, useState } from 'react'
import { motion, useScroll, useTransform, useInView } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  ArrowRight, Check, Phone, Clock, Shield, 
  Award, Home, Wrench, Calendar, MessageSquare, FileCheck, Users, Star, Hammer
} from 'lucide-react'
import { Navbar, Footer } from '../components/SharedLayout'
import MetaTags from '../components/MetaTags'
import { getSupabaseClient } from '../lib/supabaseClient'
import { notifyLeadByEmail } from '../lib/notifyLead'
import {
  marketingColors as colors,
  marketingBrand,
  PAPER,
  DARK,
  BOOKING_CALENDAR_URL,
  marketingEase as ease,
} from '../lib/marketingBrand'
import { ACCENT, MKImageFrame, PaperTextureOverlay, SketchBreak } from '../components/marketing/MarketingChrome'

const images = {
  stock20: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock20.png',
  stock8: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock8.png',
  stock12: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock12.png',
  stock16: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock16.png',
  membershipCard: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/membershipcard.png',
}

// ============================================
// HERO
// ============================================
const Hero: React.FC = () => {
  const ref = useRef<HTMLElement>(null)
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] })
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0])

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={images.stock20}
          alt="Luxury home construction"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-grey-900/95 via-grey-900/80 to-grey-900/40" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{ background: `linear-gradient(to bottom, transparent 50%, ${colors.tealDark}60)` }}
        />
      </div>
      
      <motion.div style={{ opacity }} className="relative w-full min-h-screen flex items-center pt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 w-full">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-flex items-center gap-3 px-4 py-2 bg-white/10 backdrop-blur-sm mb-8"
            >
              <Hammer className="w-4 h-4 text-white/60" />
              <span className="text-sm text-white/80">For Luxury Home Builders</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.3 }}
              className="font-serif text-white mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
            >
              Give Every Buyer a Home That's Move-In Perfect
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="text-xl lg:text-2xl leading-relaxed text-white/80 max-w-2xl mb-6 italic"
            >
              Partner with Hudson My Home™ to deliver a new standard of builder care.
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg lg:text-xl leading-relaxed text-white/60 max-w-2xl mb-10"
            >
              New construction doesn't mean maintenance-free. Hudson My Home™ partners with luxury builders across the Des Moines metro to ensure every home is professionally managed from the moment the keys are handed over. Protect your reputation, delight your buyers, and create lasting relationships — all through a single partnership.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-wrap gap-4 items-start"
            >
              <div className="flex flex-col gap-2">
                <a
                  href={BOOKING_CALENDAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase text-white transition-all duration-500 hover:brightness-110"
                  style={{ backgroundColor: colors.copper }}
                >
                  Book a Builder Partnership Call
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <p className="text-sm text-white/50 pl-1">
                  No commitment required. Cancel anytime.
                </p>
              </div>
              <a
                href="#how-it-works"
                className="group inline-flex items-center gap-3 border border-white/30 text-white px-10 py-5 text-[15px] font-medium font-sans tracking-[0.06em] uppercase transition-all duration-300 hover:bg-white/10 self-start"
              >
                See How It Works
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </section>
  )
}

// ============================================
// THE PROBLEM
// ============================================
const TheProblem: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="relative overflow-hidden py-32 md:py-40 lg:py-48" style={{ backgroundColor: colors.cream }}>
      <PaperTextureOverlay opacity={0.04} />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
              <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.copper }}>The Reality</span>
            </div>
            
            <h2 
              className="font-serif mb-8"
              style={{ color: colors.tealDark, fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              The Final Walkthrough
              <br />Isn't the Final Call.
            </h2>
            
            <div className="space-y-6 text-lg leading-[1.85] font-sans" style={{ color: colors.drillbitSkies }}>
              <p>
                Once homeowners move in, the experience is just beginning. Small issues surface. 
                Maintenance questions arise. Warranty concerns follow. And no matter the cause, 
                the first call is almost always the builder.
              </p>
              <p>
                Even exceptional homes need attentive care. Your reputation shouldn't depend on 
                how quickly post-move-in issues get addressed—and it shouldn't require your ongoing 
                involvement to protect it.
              </p>
            </div>
            
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { icon: Phone, label: 'Constant calls' },
                { icon: MessageSquare, label: 'Warranty texts' },
                { icon: Clock, label: 'Months of follow-up' },
              ].map((item) => (
                <div key={item.label} className="text-center">
                  <div 
                    className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                    style={{ backgroundColor: `${colors.teal}10` }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: colors.teal }} />
                  </div>
                  <p className="text-sm font-sans" style={{ color: colors.drillbitSkies }}>{item.label}</p>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
          >
            <MKImageFrame src={images.stock8} alt="Home builder" aspect="aspect-[4/3]" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// THE SOLUTION
// ============================================
const TheSolution: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  return (
    <section ref={ref} className="py-32 md:py-40 lg:py-48" style={{ backgroundColor: PAPER }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease }}
            className="order-2 lg:order-1"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={images.stock12}
                alt="Home care professional"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.teal }} />
              <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.teal }}>The Solution</span>
            </div>
            
            <h2 
              className="font-serif text-grey-900 mb-8"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              A Smarter Way to
              <br />Support Your Buyers.
            </h2>
            
            <div className="space-y-6 text-lg leading-relaxed text-grey-600">
              <p>
                Hudson My Home™ is a new company, but our team brings 15+ years of property maintenance 
                experience through ownership and leadership. We step in after closing to provide proactive, 
                concierge-level home care.
              </p>
              <p>
                We handle ongoing maintenance and full warranty coordination for the covered year—
                managing homeowner requests, scheduling, follow-ups, and resolution—so your team 
                stays focused on building.
              </p>
            </div>
            
            <div className="mt-10 p-6" style={{ backgroundColor: colors.cream }}>
              <p className="text-xl font-serif text-grey-900 italic">
                "Homeowners don't wait. You don't get the call."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// HOW IT WORKS
// ============================================
const HowItWorks: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const steps = [
    {
      number: '01',
      title: 'Integrate Hudson My Home™',
      description: 'Include one full year of Hudson My Home™ as part of your new construction or remodel package, or offer it as a premium enhancement.',
    },
    {
      number: '02',
      title: 'Introduce Us at Closing',
      description: 'From day one, buyers know Hudson is their point of contact for home care, maintenance, and warranty-related needs.',
    },
    {
      number: '03',
      title: 'Watch Your Brand Flourish',
      description: 'Hudson visits the home monthly, proactively addressing maintenance and fully managing warranty-related coordination during the covered year.',
    },
  ]

  return (
    <section id="how-it-works" ref={ref} className="py-32 md:py-40 lg:py-48 relative overflow-hidden" style={{ backgroundColor: colors.nearBlack }}>
      <div 
        className="absolute inset-0 opacity-30"
        style={{ background: `linear-gradient(135deg, ${colors.tealDark}40, transparent 50%)` }}
      />
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 relative">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-4 mb-6"
          >
            <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
            <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.copper }}>The Process</span>
            <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="font-serif text-white"
            style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            How Builders Partner
            <br /><span style={{ color: colors.copper }}>With Hudson My Home™</span>
          </motion.h2>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, ease, delay: 0.2 + i * 0.1 }}
              className="relative"
            >
              <div className="p-8 lg:p-10 bg-white/5 border border-white/10 h-full">
                <span 
                  className="text-5xl lg:text-6xl font-serif mb-6 block"
                  style={{ color: colors.copper, opacity: 0.5 }}
                >
                  {step.number}
                </span>
                <h3 className="text-xl lg:text-2xl font-serif text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-white/60 text-lg leading-relaxed">
                  {step.description}
                </p>
              </div>
              
              {i < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-6 lg:-right-8 transform -translate-y-1/2">
                  <ArrowRight className="w-6 h-6 text-white/20" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// WHAT WE HANDLE
// ============================================
const WhatWeHandle: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const services = [
    { icon: Calendar, title: 'Monthly Visits', desc: 'Thorough inspections every month, catching issues before they escalate.' },
    { icon: Wrench, title: 'Maintenance Coordination', desc: 'We handle all routine and proactive maintenance needs.' },
    { icon: FileCheck, title: 'Warranty Management', desc: 'Full warranty coordination—requests, scheduling, follow-up, resolution.' },
    { icon: Shield, title: 'Quality Assurance', desc: 'Every service vetted and overseen to your standards.' },
    { icon: Users, title: 'Homeowner Communication', desc: 'We become the point of contact, so you stay focused on building.' },
    { icon: Award, title: 'Brand Protection', desc: 'Your reputation protected through exceptional post-closing care.' },
  ]

  return (
    <section ref={ref} className="py-32 md:py-40 lg:py-48" style={{ backgroundColor: colors.cream }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-4 mb-6"
          >
            <div className="w-8 h-[1px] bg-grey-300" />
            <span className="text-sm tracking-[0.15em] uppercase text-grey-400">What We Handle</span>
            <div className="w-8 h-[1px] bg-grey-300" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
            className="font-serif text-grey-900"
            style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            Complete Post-Closing Care
          </motion.h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, ease, delay: 0.1 + i * 0.05 }}
              className="p-8 bg-white"
            >
              <div 
                className="w-14 h-14 mb-6 flex items-center justify-center"
                style={{ backgroundColor: `${colors.teal}10` }}
              >
                <service.icon className="w-6 h-6" style={{ color: colors.teal }} />
              </div>
              <h3 className="text-xl font-serif text-grey-900 mb-3">{service.title}</h3>
              <p className="text-grey-600 leading-relaxed">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

// ============================================
// BUYER BENEFITS
// ============================================
const BuyerBenefits: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })

  const benefits = [
    'Their own Hudson — a dedicated home professional from day one',
    'Monthly in-home maintenance visits',
    'Complete warranty coordination',
    'Proactive issue prevention',
    '24/7 priority support line',
    'Full documentation of all visits',
  ]

  return (
    <section ref={ref} className="py-32 md:py-40 lg:py-48" style={{ backgroundColor: PAPER }}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
              <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.copper }}>What Buyers Get</span>
            </div>
            
            <h2 
              className="font-serif text-grey-900 mb-8"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              An Experience That
              <br />Matches Your Craftsmanship.
            </h2>
            
            <p className="text-lg leading-relaxed text-grey-600 mb-10">
              Your buyers receive white-glove home care that reflects the quality of your work. 
              A dedicated Hudson professional becomes their trusted partner for the entire first year.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.05 }}
                  className="flex items-center gap-3"
                >
                  <Check className="w-5 h-5 flex-shrink-0" style={{ color: colors.teal }} />
                  <span className="text-grey-700">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={images.stock16}
                alt="Happy homeowners"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Membership Card Overlay */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="absolute -bottom-8 -left-8 w-48 lg:w-56 shadow-2xl"
            >
              <img 
                src={images.membershipCard}
                alt="Hudson Membership Card"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// APPLICATION FORM
// ============================================
const ApplicationForm: React.FC = () => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const [submitted, setSubmitted] = useState(false)
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    homesPerYear: '',
    avgHomePrice: '',
    message: '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)
    try {
      const supabase = getSupabaseClient()
      const payload = {
        companyName: formData.companyName,
        homesPerYear: formData.homesPerYear,
        avgHomePrice: formData.avgHomePrice,
        message: formData.message,
      }
      const { error } = await supabase.from('leads').insert({
        source: 'builder',
        full_name: formData.contactName,
        email: formData.email,
        phone: formData.phone?.trim() || null,
        payload,
      })
      if (error) throw error

      try {
        await notifyLeadByEmail({
          source: 'builder',
          full_name: formData.contactName,
          email: formData.email,
          phone: formData.phone || undefined,
          payload,
        })
      } catch (notifyErr) {
        console.warn('[BuildersPage] notifyLeadByEmail failed:', notifyErr)
      }

      setSubmitted(true)
    } catch (err) {
      console.error('[BuildersPage] Supabase insert error (full):', err)
      if (err && typeof err === 'object') {
        const errObj = err as Record<string, unknown>
        console.error('[BuildersPage] Supabase error details:', {
          message: errObj.message,
          code: errObj.code,
          details: errObj.details,
          hint: errObj.hint,
          ...errObj,
        })
      }
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  return (
    <section id="apply" ref={ref} className="py-32 md:py-40 lg:py-48 relative overflow-hidden" style={{ backgroundColor: colors.nearBlack }}>
      <div 
        className="absolute inset-0 opacity-30"
        style={{ background: `linear-gradient(135deg, ${colors.tealDark}40, transparent 50%)` }}
      />
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 relative">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-8 h-[1px]" style={{ backgroundColor: colors.copper }} />
              <span className="text-sm tracking-[0.15em] uppercase" style={{ color: colors.copper }}>Partner With Us</span>
            </div>
            
            <h2 
              className="font-serif text-white mb-6"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
            >
              Ready to Elevate Your
              <br />Buyer Experience?
            </h2>
            
            <p className="text-xl text-white/60 leading-relaxed mb-10">
              Add Hudson My Home™ to your buyer package. Protect your reputation, 
              delight your clients, and stay focused on what you do best—building.
            </p>
            
            <div className="space-y-6">
              {[
                { icon: Star, text: 'Enhance your brand value' },
                { icon: Shield, text: 'Protect your reputation post-closing' },
                { icon: Users, text: 'Create referral-worthy experiences' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div 
                    className="w-12 h-12 flex items-center justify-center"
                    style={{ backgroundColor: `${colors.teal}20` }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: colors.copper }} />
                  </div>
                  <span className="text-white/80 text-lg">{item.text}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-12 pt-12 border-t border-white/10">
              <p className="text-white/40 text-sm mb-2">Contact us directly</p>
              <a 
                href="mailto:info@hudsonmyhome.com"
                className="text-lg font-medium transition-colors hover:opacity-80"
                style={{ color: colors.copper }}
              >
                info@hudsonmyhome.com
              </a>
            </div>
          </motion.div>
          
          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease, delay: 0.2 }}
          >
            {!submitted ? (
              <form onSubmit={handleSubmit} className="p-8 lg:p-10 bg-white">
                <h3 className="font-serif text-2xl text-grey-900 mb-8">Start the Conversation</h3>
                
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Company Name *</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Contact Name *</label>
                      <input
                        type="text"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Homes Built Per Year</label>
                      <select
                        name="homesPerYear"
                        value={formData.homesPerYear}
                        onChange={handleChange}
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      >
                        <option value="">Select range</option>
                        <option value="1-10">1-10 homes</option>
                        <option value="11-25">11-25 homes</option>
                        <option value="26-50">26-50 homes</option>
                        <option value="51-100">51-100 homes</option>
                        <option value="100+">100+ homes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-grey-600 mb-2">Average Home Price</label>
                      <select
                        name="avgHomePrice"
                        value={formData.avgHomePrice}
                        onChange={handleChange}
                        className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors"
                      >
                        <option value="">Select range</option>
                        <option value="500-750k">$500K - $750K</option>
                        <option value="750k-1m">$750K - $1M</option>
                        <option value="1m-2m">$1M - $2M</option>
                        <option value="2m+">$2M+</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-grey-600 mb-2">Message</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Tell us about your company and how you'd like to partner..."
                      className="w-full px-4 py-4 bg-grey-50 border border-grey-200 focus:outline-none focus:border-grey-400 transition-colors resize-none"
                    />
                  </div>
                  
                  {submitError && <p className="text-red-600 text-sm">{submitError}</p>}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-3 py-5 text-lg font-medium text-white transition-all duration-300 hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed"
                    style={{ backgroundColor: colors.teal }}
                  >
                    {submitting ? (
                      <>
                        <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Partnership Inquiry
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="p-8 lg:p-10 bg-white text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.teal}15` }}>
                  <Check className="w-8 h-8" style={{ color: colors.teal }} />
                </div>
                <h3 className="font-serif text-2xl text-grey-900 mb-4">Inquiry Received</h3>
                <p className="text-grey-600 mb-6">
                  Thank you for your interest in partnering with Hudson My Home. 
                  Our team will be in touch within 24 hours.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-lg font-medium transition-colors hover:opacity-80"
                  style={{ color: colors.teal }}
                >
                  Return Home
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// ============================================
// PAGE
// ============================================
const BuildersPage: React.FC = () => (
  <div className="min-h-screen" style={{ backgroundColor: marketingBrand.tapeTurquoise }}>
    <MetaTags
      title="Builder Partnerships | Hudson My Home"
      description="Differentiate your builds with Hudson My Home. Every new homeowner gets their own Hudson — a dedicated professional from day one."
    />
    <Navbar />
    <main>
      <Hero />
      <SketchBreak accentSrc={ACCENT.penBlueprint} accentOpacity={0.24} />
      <TheProblem />
      <TheSolution />
      <HowItWorks />
      <WhatWeHandle />
      <BuyerBenefits />
      <ApplicationForm />
    </main>
    <Footer />
  </div>
)

export default BuildersPage
